import { ArrowUpRight, Clock } from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionLabel from "@/components/SectionLabel";
import { BLOG_POSTS } from "@/data/site";
import SEO from "@/components/SEO";

export default function Blog() {
  const [featured, ...rest] = BLOG_POSTS;

  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "AmagicTech Field Notes & Engineering Blog",
    "url": "https://amagictechsolutions.com/blog",
    "description": "Thoughts on engineering, AI adoption, SaaS scaling, and product design from the AmagicTech team.",
    "publisher": {
      "@type": "Organization",
      "name": "AmagicTech Solutions",
      "logo": "https://amagictechsolutions.com/brand/favicon-512.png"
    },
    "blogPost": BLOG_POSTS.map((post) => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.excerpt,
      "datePublished": post.date,
      "author": {
        "@type": "Organization",
        "name": "AmagicTech Solutions"
      },
      "url": `https://amagictechsolutions.com/blog#${post.slug}`
    }))
  };

  return (
    <main data-testid="blog-page" className="pt-20">
      <SEO 
        title="Blog & Field Notes"
        description="Read the latest engineering insights on AI copilots, SaaS scaling, composable commerce, and cross-platform mobile apps from the AmagicTech team."
        url="/blog"
        keywords="engineering blog, AI copilots, composable commerce, react native vs flutter, software architecture, tech insights"
        schema={blogSchema}
      />
      <section className="container-tight">
        <Reveal>
          <SectionLabel testid="blog-label">Field notes</SectionLabel>
        </Reveal>
        <Reveal delay={0.05}>
          <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter text-zinc-900 leading-[0.98]">
            Writing from
            <br /><span className="text-zinc-500">the workshop.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-8 max-w-2xl text-zinc-600 text-lg leading-relaxed">
            Notes on engineering, design, AI adoption, and product growth from the people
            building AmagicTech&apos;s projects every day.
          </p>
        </Reveal>
      </section>

      {/* FEATURED */}
      <section className="container-tight mt-16">
        <Reveal>
          <a
            href="#"
            data-testid="blog-featured"
            className="group block border border-zinc-200 hover:border-zinc-400 transition-colors p-8 md:p-12"
          >
            <div className="grid md:grid-cols-12 gap-8 items-end">
              <div className="md:col-span-2">
                <p className="label-mono">{featured.tag}</p>
                <p className="font-mono-tech text-xs text-zinc-500 mt-3">{featured.date}</p>
              </div>
              <div className="md:col-span-8">
                <h2 className="font-display text-3xl md:text-5xl text-zinc-900 tracking-tighter leading-[1.05] group-hover:opacity-90">
                  {featured.title}
                </h2>
                <p className="mt-4 text-zinc-600 leading-relaxed text-lg max-w-2xl">
                  {featured.excerpt}
                </p>
              </div>
              <div className="md:col-span-2 flex md:justify-end">
                <span className="inline-flex items-center gap-2 text-zinc-900 text-sm group-hover:translate-x-0.5 transition-transform">
                  Read <ArrowUpRight className="w-4 h-4" />
                </span>
              </div>
            </div>
          </a>
        </Reveal>
      </section>

      {/* GRID */}
      <section className="container-tight mt-16 pb-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {rest.map((p, idx) => (
          <Reveal key={p.slug} delay={idx * 0.05}>
            <a
              href="#"
              data-testid={`blog-card-${idx}`}
              className="group block border border-zinc-200 hover:border-zinc-400 transition-colors p-7 h-full"
            >
              <div className="flex items-center justify-between">
                <span className="label-mono">{p.tag}</span>
                <span className="inline-flex items-center gap-1.5 font-mono-tech text-[11px] text-zinc-500">
                  <Clock className="w-3 h-3" /> {p.read}
                </span>
              </div>
              <h3 className="mt-8 font-display text-2xl text-zinc-900 tracking-tight leading-snug">
                {p.title}
              </h3>
              <p className="mt-3 text-zinc-600 text-[15px] leading-relaxed">{p.excerpt}</p>
              <div className="mt-8 flex items-center justify-between text-xs">
                <span className="font-mono-tech text-zinc-500">{p.date}</span>
                <ArrowUpRight className="w-4 h-4 text-zinc-500 group-hover:text-zinc-900 transition-colors" />
              </div>
            </a>
          </Reveal>
        ))}
      </section>
    </main>
  );
}
